//#region Imports & External Dependencies
import { computed, makeObservable, observable, runInAction } from "mobx";
import { ViewModelBase } from "@shoothill/core";
import { APIClient, ICommand, IKeyState, RelayCommand } from "Application";
import { CurrentMentorsModel } from "./CurrentMentorsModel";
import { AppUrls } from "AppUrls";
import { MentorModel } from "Views/Mentors/Mentors/MentorModel";
import toast from "react-hot-toast";
import { ISearchBoxProps } from "Components/SearchBox/SearchBox";
import { GetFilteredMentorsRequest, GetFilteredMentorsResponse, POST_GetFilteredMentorsEndpoint } from "Views/Mentors/Mentors/EndPoints/POST_GetFilteredMentorsEndpoint";
import { MentorStatusEnum } from "Views/Mentors/Mentors/Enums/MentorStatusEnum";
import { POST_UpdateMentorStatusEndpoint, POST_UpdateMentorStatusRequest } from "Views/Mentors/Mentors/EndPoints/POST_UpdateMentorStatusEndpoints";
//#endregion

//#region Class Definition
export class CurrentMentorsViewModel extends ViewModelBase<CurrentMentorsModel> {
    //#region Properties & State
    public apiClient = new APIClient();
    public mentorToUpdateStatus: MentorModel | null = null;
    public _isLoadingData: boolean = false;
    public _render: boolean = false;
    public loadTimeoutId: NodeJS.Timeout | null = null;
    public onConfirmMentorStatusChangeCommand: ICommand;
    //#endregion

    //#region Constructor/Initialization
    constructor(onConfirmMentorStatusChangeCommand: ICommand) {
        super(new CurrentMentorsModel());
        makeObservable(this, {
            mentorToUpdateStatus: observable,
            _isLoadingData: observable,
            _render: observable,
            loadTimeoutId: observable,
            mentors: computed,
            isLoadingData: computed,
            render: computed,
        });
        this.onConfirmMentorStatusChangeCommand = onConfirmMentorStatusChangeCommand;
    }
    //#endregion

    //#region Computed Properties & Getters
    public get mentors() {
        return this.model.mentors.slice();
    }

    public get isLoadingData() {
        return this._isLoadingData;
    }

    public get render() {
        return this._render;
    }

    public get showMentorStatusChangeWarning() {
        return this.mentorToUpdateStatus !== null;
    }

    public get searchBoxProps(): ISearchBoxProps {
        return {
            value: this.model.filterKeyword,
            command: this.updateFilterKeywordCommand,
        };
    }

    //#endregion

    //#region Public Methods & Commands
    public setIsLoadingData = (isLoading: boolean) => {
        this._isLoadingData = isLoading;
    };

    public setRender = (render: boolean) => {
        this._render = render;
    };

    public reset = () => {
        this.setRender(false);
        this.setIsLoadingData(false);
        this.model.filterKeyword = "";
    };

    public setLoadTimeoutId = (loadTimeoutId: NodeJS.Timeout) => {
        this.loadTimeoutId = loadTimeoutId;
    };

    public navigateToMentorDetailsCommand = new RelayCommand((id: string) => {
        this.history.push(AppUrls.Client.Mentors.MentorDetail.Edit.replace(":id", id));
    });

    public updateFilterKeywordCommand = new RelayCommand((keyword: string) => {
        this.onUpdateKeywordSearch(keyword);
    });

    public navigateToAddMentorCommand = new RelayCommand(
        () => {
            this.history.push(AppUrls.Client.Mentors.MentorDetail.Root);
        },
        () => this.apiClient.IsBusy === false,
    );

    public moveToPastMentorsCommand = new RelayCommand((mentorToUpdate: MentorModel) => {
        runInAction(() => {
            this.mentorToUpdateStatus = mentorToUpdate;
        });
    });
    public dismissStatusChangeWarningCommand = new RelayCommand(() => {
        runInAction(() => {
            this.mentorToUpdateStatus = null;
        });
    });

    public confirmMoveToPastMentorsCommand = new RelayCommand(async () => {
        if (!this.mentorToUpdateStatus) return;
        const request: POST_UpdateMentorStatusRequest = {
            mentorId: this.mentorToUpdateStatus.id!,
            statusType: MentorStatusEnum.Past,
        };
        await this.apiClient.sendAsync(new POST_UpdateMentorStatusEndpoint(), request);
        if (this.apiClient.IsRequestSuccessful) {
            toast.success("Mentor moved to Past Mentors successfully");
            // Refresh the list after status update
            this.getFilteredMentors();
            // Clear the selected mentor
            runInAction(async () => {
                const currentMentors = this.model.mentors.slice().filter((m) => m.id !== this.mentorToUpdateStatus?.id);
                this.model.mentors.replace(currentMentors);
                this.mentorToUpdateStatus = null;
                this.onConfirmMentorStatusChangeCommand.execute();
            });
        } else {
            this.mentorToUpdateStatus = null;
            toast.error("Error updating mentor status");
        }
    });

    public resetApiClientErrorCommand = new RelayCommand(() => {
        this.apiClient.reset();
    });
    //#endregion

    //#region Event Handlers
    public onUpdateKeywordSearch = (v: string) => {
        const previousSearchString = this.model.filterKeyword;
        this.setValue("filterKeyword", v);
        const isEmptySearch = this.model.filterKeyword.length === 0;
        const isValidSearch = this.model.filterKeyword.length > 1 && this.model.filterKeyword !== previousSearchString;
        this.setValue("filterKeyword", v);
        if (isEmptySearch || isValidSearch) {
            this.debouncedLoadMentorsAsync();
        }
    };
    //#endregion

    //#region API Methods
    public getFilteredMentors = async () => {
        this.setIsLoadingData(true);
        const request: GetFilteredMentorsRequest = new GetFilteredMentorsRequest();
        request.keyword = this.model.filterKeyword;
        request.statusType = MentorStatusEnum.Current;
        await this.apiClient.sendAsync(new POST_GetFilteredMentorsEndpoint(), request);
        if (this.apiClient.IsRequestSuccessful) {
            runInAction(() => {
                const response: GetFilteredMentorsResponse = this.apiClient.Response();
                const models = response.items.map((i) => {
                    const m = new MentorModel();
                    m.fromResponse(i);
                    return m;
                });
                this.model.mentors.replace(models);
            });
            this.setIsLoadingData(false);
            this.setRender(true);
        } else {
            this.setIsLoadingData(false);
        }
    };
    //#endregion

    //#region Helper Methods
    public debouncedLoading = (callback: () => void, delayMs: number = 500) => {
        if (this.loadTimeoutId) {
            clearTimeout(this.loadTimeoutId);
        }

        this.setLoadTimeoutId(
            setTimeout(() => {
                callback();
            }, delayMs),
        );
    };

    public debouncedLoadMentorsAsync = () => {
        this.debouncedLoading(this.getFilteredMentors, 500);
    };
    //#endregion
}
//#endregion
